import { useEffect, useRef, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import { AddressPick, loadDaumPostcode, toAddressPick } from "@/lib/daum-postcode";

interface DaumPostcodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (pick: AddressPick) => void;
  // 닫힌 뒤 포커스를 옮길 곳 — 없으면 Radix 기본(연 버튼)으로 돌아간다.
  onCloseAutoFocus?: (event: Event) => void;
}

// 다음 쪽 기본 색을 그대로 쓴다 — theme 는 #hex 라 토큰 규칙에 걸린다.
const PostcodeEmbed = ({ onComplete }: { onComplete: (pick: AddressPick) => void }) => {
  const box = useRef<HTMLDivElement>(null);
  const latest = useRef(onComplete);
  latest.current = onComplete;
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let alive = true;
    const el = box.current;
    loadDaumPostcode()
      .then(() => {
        if (!alive || !el || !window.daum) return;
        new window.daum.Postcode({
          width: "100%",
          height: "100%",
          maxSuggestItems: 5,
          oncomplete: (data) => latest.current(toAddressPick(data)),
        }).embed(el);
        setState("ready");
      })
      .catch(() => {
        if (alive) setState("error");
      });
    return () => {
      alive = false;
      el?.replaceChildren();
    };
  }, []);

  return (
    <div className="relative h-[min(28rem,65dvh)] w-full overflow-hidden rounded-md border border-border">
      <div ref={box} className="size-full" />
      {state === "loading" && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">주소 검색을 불러오는 중…</p>
      )}
      {state === "error" && (
        <p role="alert" className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-destructive">
          주소 검색을 불러오지 못했습니다. 잠시 뒤 다시 열거나 주소를 직접 적어 주세요.
        </p>
      )}
    </div>
  );
};

const DaumPostcodeDialog = ({ open, onOpenChange, onComplete, onCloseAutoFocus }: DaumPostcodeDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl gap-3 p-4 sm:p-6" onCloseAutoFocus={onCloseAutoFocus}>
      <DialogHeader>
        <DialogTitle>주소 검색</DialogTitle>
        <DialogDescription>도로명·건물명·지번으로 찾은 뒤 고르세요.</DialogDescription>
      </DialogHeader>
      {open && <PostcodeEmbed onComplete={onComplete} />}
    </DialogContent>
  </Dialog>
);

export default DaumPostcodeDialog;
